/**
 * AccordionAtom.tsx
 *
 * A generic accordion atom wrapping PrimeReact's Accordion and AccordionTab components
 * with design system styling.
 */

import React from 'react';
import {
  Accordion,
  AccordionProps,
  AccordionTab,
  AccordionTabProps,
} from 'primereact/accordion';
import type { AccordionTabChangeEvent as PrimeAccordionTabChangeEvent } from 'primereact/accordion';
import './AccordionAtom.scss';

/** Re-export the AccordionTabChangeEvent type from PrimeReact */
export type AccordionTabChangeEvent = PrimeAccordionTabChangeEvent;

export type AccordionVariant = 'default' | 'bordered' | 'filled';

export interface AccordionAtomProps extends Omit<AccordionProps, 'className'> {
  /** Visual variant of the accordion */
  variant?: AccordionVariant;
  /** Additional CSS class names */
  className?: string;
  /** Children - should be AccordionTabAtom components */
  children?: React.ReactNode;
}

export interface AccordionTabAtomProps extends AccordionTabProps {
  /** Additional CSS class names */
  className?: string;
  /** Children content */
  children?: React.ReactNode;
}

const AccordionAtom: React.FC<AccordionAtomProps> = ({
  variant = 'default',
  className = '',
  children,
  ...rest
}) => {
  // Build CSS classes
  const classes = [
    'accordion-atom',
    `accordion-atom--variant-${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    <Accordion className={classes} {...rest}>
      {children}
    </Accordion>
  );
};

const AccordionTabAtom: React.FC<AccordionTabAtomProps> = ({
  className = '',
  children,
  ...rest
}) => {
  const classes = ['accordion-tab-atom', className].filter(Boolean).join(' ').trim();

  return (
    <AccordionTab className={classes} {...rest}>
      {children}
    </AccordionTab>
  );
};

AccordionAtom.displayName = 'AccordionAtom';
AccordionTabAtom.displayName = 'AccordionTab';

export { AccordionTabAtom };
export default AccordionAtom;
